import { generateClient } from 'aws-amplify/api';
import { MDBBtn, MDBCard, MDBCardBody, MDBCardTitle, MDBIcon, MDBRow, MDBTable, MDBTableBody, MDBTableHead, MDBTabs, MDBTabsItem, MDBTabsLink, MDBTabsContent, MDBTabsPane, MDBInput, MDBSelect, MDBCol, MDBAccordion, MDBAccordionItem, MDBCheckbox, MDBSpinner, MDBTypography, MDBContainer, MDBCardImage, MDBCardText, MDBChip, MDBCardFooter, MDBRadio } from "mdb-react-ui-kit";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { DevContext, UserContext } from "../../App";
import { getLocalizedText } from "../../utils/localizedText";
import { useIndustry } from "../../utils/industryContextHook";
import useDebounce from "../../utils/debounce";
import { exists } from "i18next";
import { useNavigate } from "react-router-dom";
import { getUrl } from "aws-amplify/storage";
import { type } from "@testing-library/user-event/dist/type";
import { captureError } from '../../utils/sentryHelpers';
import {
    LOUNGES_2026_EVENT_ID,
    LOUNGES_2026_INSIGHT_VISITOR_PRODUCT_ID,
    LOUNGES_2026_INSIGHT_NON_VISITOR_PRODUCT_ID,
    LOUNGES_2026_INSIGHT_VISITOR_PRICE,
    LOUNGES_2026_INSIGHT_NON_VISITOR_PRICE,
    LOUNGES_INDUSTRY_ID,
} from "./insightAccessConfig";

const client = generateClient();

export default function AllEventLectures() {
    const { t, i18n } = useTranslation();
    const { isDevMode } = useContext(DevContext);
    const { user } = useContext(UserContext);
    const { currentIndustry } = useIndustry();

    const [filters, setFilters] = useState({
        type: null,
        selectedTopics: [],
        selectedTypes: [],
        selectedEventId: null,
    });

    const [lectures, setLectures] = useState([]);
    
    // Separate search state that doesn't trigger API calls
    const [searchTerm, setSearchTerm] = useState("");

    // Company search and filter
    const [companySearchTerm, setCompanySearchTerm] = useState("");
    const [companySearchResults, setCompanySearchResults] = useState([]);
    const [selectedCompanies, setSelectedCompanies] = useState([]);
    const debouncedCompanySearchTerm = useDebounce(companySearchTerm, 1000);

    // Filter Selectors
    const [events, setEvents] = useState([]);
    const [userAttendedEvents, setUserAttendedEvents] = useState([]);
    const [hasLounges2026ScannedTicket, setHasLounges2026ScannedTicket] = useState(false);
    const [lounges2026Event, setLounges2026Event] = useState(null);
    const [topicGroups, setTopicGroups] = useState([]);

    // Loading states
    const [isLoadingLectures, setIsLoadingLectures] = useState(true);

    const handleCompanyInputBlur = () => {
        setTimeout(() => {
            setCompanySearchResults([]);
            setCompanySearchTerm("");
        }, 150);
    };

    // Company search function
    const searchCompanies = async (term) => {
        if (term.length < 2) {
            setCompanySearchResults([]);
            return;
        }
        
        try {
            const response = await client.graphql({
                query: /* GraphQL */ `
                    query SearchCompanies($filter: SearchableCompanyFilterInput, $limit: Int) {
                        searchCompanies(filter: $filter, limit: $limit) {
                            items {
                                id
                                name
                                image {
                                    main {
                                        fileName
                                        alt
                                    }
                                }
                            }
                        }
                    }
                `,
                variables: {
                    filter: { name: { matchPhrasePrefix : term } },
                    limit: 5
                }
            });
            
            setCompanySearchResults(response.data.searchCompanies.items);
        } catch (error) {
            console.error("Error searching companies:", error);
            captureError(error, {
                feature: 'lectures',
                operation: 'search-companies',
                context: { searchTerm: term }
            });
            setCompanySearchResults([]);
        }
    };

    // Helper functions to manage selected companies
    const addCompany = (company) => {
        if (!selectedCompanies.some(c => c.id === company.id)) {
            setSelectedCompanies([...selectedCompanies, company]);
            setFilters(prev => ({
                ...prev,
                companies: [...(prev.companies || []), company]
            }));
        }
        setCompanySearchTerm("");
        setCompanySearchResults([]);
    };

    const removeCompany = (companyId) => {
        setSelectedCompanies(selectedCompanies.filter(c => c.id !== companyId));
        setFilters(prev => ({
            ...prev,
            companies: (prev.companies || []).filter(c => c.id !== companyId)
        }));
    };

    
    
    // Topics data
      const getTopics = async () => {
          try {
            const topicGroupsWithTopics = await client.graphql({
              query: /* GraphQL */ `
              query ListTopicGroups(
                $filter: ModelTopicGroupFilterInput
                $limit: Int
                $nextToken: String
              ) {
                listTopicGroups(filter: $filter, limit: $limit, nextToken: $nextToken) {
                  items {
                    id
                    sortKey
                    color
                    name {
                      language
                      text
                    }
                    topics {
                      items {
                        id
                        name {
                          language
                          text
                        }
                        topicGroupTopicsId
                      }
                    }
                  }
                  nextToken
                  __typename
                }
              }
            `
              });
              setTopicGroups(topicGroupsWithTopics.data.listTopicGroups.items.sort((a, b) => a.sortKey - b.sortKey));
          } catch (error) {
              console.log("error on fetching topics", error);
              captureError(error, {
                  feature: 'lectures',
                  operation: 'fetch-topic-groups'
              });
          }
      }
    
      

    const handleClickTopicGroup = (topicGroupId) => {
        setFilters(prev => {
            const group = topicGroups.find(group => group.id === topicGroupId);
            const allSelected = group.topics.items.every(topic => prev.selectedTopics.includes(topic.id));
            const newSelectedTopics = allSelected
                ? prev.selectedTopics.filter(topicId => !group.topics.items.map(topic => topic.id).includes(topicId))
                : [...prev.selectedTopics, ...group.topics.items.map(topic => topic.id)];
            return {
                ...prev,
                selectedTopics: newSelectedTopics
            };
        });
    };

    const handleClickTopic = (topicId) => {
        setFilters(prev => ({
            ...prev,
            selectedTopics: prev.selectedTopics.includes(topicId)
                ? prev.selectedTopics.filter(id => id !== topicId)
                : [...prev.selectedTopics, topicId]
        }));
    };


    // Lädt das LOUNGES-2026-Event vollständig (für die Event-Liste, wenn der
    // User kein 2026-Ticket hat) und setzt nebenbei lounges2026Event fürs Banner.
    const fetchLounges2026EventFull = async () => {
        try {
            const res = await client.graphql({
                query: /* GraphQL */ `
                    query GetEvent($id: ID!) {
                        getEvent(id: $id) {
                            id
                            type
                            name {
                                language
                                text
                            }
                            status
                            startDate
                            endDate
                            publicVideos
                            exhibitorLectures
                            image {
                                fileName
                                alt
                            }
                        }
                    }
                `,
                variables: { id: LOUNGES_2026_EVENT_ID }
            });
            const event = res.data?.getEvent;
            if (event) {
                setLounges2026Event(event);
            }
            return event;
        } catch (error) {
            captureError(error, {
                feature: 'lectures',
                operation: 'fetch-lounges-2026-event'
            });
            return null;
        }
    };

    const fetchEvents = async () => {
        try {
            const ticketsResponse = await client.graphql({
                query: /* GraphQL */ `
                    query GetUser($id: ID!) {
                        getUser(id: $id) {
                            id
                            owner
                            tickets {
                                items {
                                    ticketEventId
                                    paymentStatus
                                    scans {
                                        items {
                                            id
                                        }
                                    }
                                    event {
                                        id
                                        type
                                        name {
                                            language
                                            text
                                        }
                                        status
                                        startDate
                                        endDate
                                        publicVideos
                                        exhibitorLectures
                                        image {
                                            fileName
                                            alt
                                        }
                                        industries {
                                            items {
                                                industry {
                                                    id
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                `,
                variables: {
                    id: user.id
                }
            });

            const userTickets = ticketsResponse.data.getUser.tickets.items
                .filter(ticket => ticket.event)
                // Für Seminare zählen auch "pending"-Tickets — Zahlung läuft dort
                // i.d.R. per Rechnung nach dem Termin. TODO: durch saubere
                // Lösung ersetzen, wenn der Seminar-Payment-Flow gefixt ist.
                .filter(ticket =>
                    ticket.paymentStatus === "paid" ||
                    (ticket.paymentStatus === "pending" && ticket.event.type === "seminar")
                );

            const userEvents = userTickets
                .map(ticket => ticket.event)
                .filter(event => event.exhibitorLectures !== false)
                .filter(event =>
                    event.industries.items.some(industry => industry.industry.id === currentIndustry.id)
                );

            const eventMap = new Map();
            userEvents.forEach(event => eventMap.set(event.id, event));

            // HARDCODE: LOUNGES 2026 immer anzeigen — auch ohne Ticket, damit
            // Nicht-Besucher die Vorträge sehen können (bevor sie sie kaufen).
            // Video/Dokumente bleiben für sie deaktiviert (userAttendedEvents /
            // hasInsightAccess steuern den Zugriff unabhängig davon).
            // TODO: durch eine generische Lösung ersetzen.
            if (!eventMap.has(LOUNGES_2026_EVENT_ID) && currentIndustry?.id === LOUNGES_INDUSTRY_ID) {
                const lounges26 = await fetchLounges2026EventFull();
                if (lounges26) {
                    eventMap.set(LOUNGES_2026_EVENT_ID, lounges26);
                }
            }

            const sortedEvents = Array.from(eventMap.values())
                .sort((a, b) => new Date(b.startDate) - new Date(a.startDate));

            setEvents(sortedEvents);
            setUserAttendedEvents(userTickets.map(ticket => ticket.event));

            // Check if user has a scanned ticket for LOUNGES 2026 (qualifies for discount)
            const lounges26Ticket = userTickets.find(t => t.event?.id === LOUNGES_2026_EVENT_ID);
            setHasLounges2026ScannedTicket(!!(lounges26Ticket?.scans?.items?.length));
            if (lounges26Ticket?.event) {
                setLounges2026Event(lounges26Ticket.event);
            }
            
            // Setze das neueste Event als Default
            setFilters(prev => ({
                ...prev,
                selectedEventId: sortedEvents.length > 0 ? sortedEvents[0].id : null
            }));
        } catch (error) {
            console.error("Error fetching events:", error);
            captureError(error, {
                feature: 'lectures',
                operation: 'fetch-events',
                context: { userId: user?.id, industryId: currentIndustry?.id }
            });
        }
    };

    // Fetch lectures
    const fetchLectures = async () => {
        setIsLoadingLectures(true);
        try {
            const searchLectureFilter = {
                and: [
                    {approved: { eq: true }}
                ]
            };

            // Add event or default all available events
            if(filters.selectedEventId) {
                searchLectureFilter.and.push({ eventLecturesId: { eq: filters.selectedEventId } });
            }

            if (filters.selectedTypes && filters.selectedTypes.length > 0) {
                // Wenn spezifische Typen ausgewählt sind, suche nur nach diesen
                searchLectureFilter.and.push({
                    or: filters.selectedTypes.map(type => ({ type: { eq: type } }))
                });
            } else {
                searchLectureFilter.and.push({
                    and: [
                        { type: { ne: "promotion_marketing" } },
                        { type: { ne: "promotion_technical" } },
                        { type: { ne: "recruiting" } }
                    ]
                });
            }

            // Add company filter
            if (selectedCompanies.length > 0) {
                searchLectureFilter.and.push({
                    or: selectedCompanies.map((company) => ({ 
                        companyLecturesId: { eq: company.id } 
                    }))
                });
            }

            // Add media filters
            if (filters.pdf) {
                searchLectureFilter.and.push({
                    or: [
                        { pdfDe: { ne: null } },
                        { pdfEn: { ne: null } }
                    ]
                });
            }

            if (filters.video) {
                searchLectureFilter.and.push({ videoS3Key: { exists: true } });
            }

            // if(filters.pdf) {
            //     searchLectureFilter.and.push({
            //         or: [
            //             { pdfDe: { exists: true } },
            //             { pdfEn: { exists: true } }
            //         ]
            //     });
            // }

            if (filters.selectedTopics && filters.selectedTopics.length > 0) {
                let lecturesinTopicIds = [];
                
                // Helper function to fetch all lecture topics with pagination
                const fetchAllLectureTopics = async (topicId, allItems = [], nextToken = null) => {
                    try {
                        const response = await client.graphql({
                            query: /* GraphQL */ `
                                query ListLecturesTopics($filter: ModelLecturesTopicsFilterInput, $limit: Int, $nextToken: String) {
                                    listLecturesTopics(filter: $filter, limit: $limit, nextToken: $nextToken) {
                                        items {
                                            lecture {
                                                id
                                            }
                                            topic {
                                                id
                                            }
                                        }
                                        nextToken
                                    }
                                }
                            `,
                            variables: {
                                filter: { topicId: { eq: topicId } },
                                limit: 1000,
                                nextToken: nextToken
                            }
                        });

                        // Filter out items with null lecture fields to avoid processing orphaned records
                        const validItems = response.data.listLecturesTopics.items.filter(item => item?.lecture?.id);
                        const newItems = [...allItems, ...validItems];

                        // If there's a nextToken, fetch the next page
                        if (response.data.listLecturesTopics.nextToken) {
                            return fetchAllLectureTopics(topicId, newItems, response.data.listLecturesTopics.nextToken);
                        }

                        return newItems;
                    } catch (error) {
                        // Check if this is a GraphQL error about null lecture fields
                        const isNullLectureError = error?.errors?.some(err =>
                            err.message?.includes('Cannot return null for non-nullable type') &&
                            err.message?.includes('lecture')
                        );

                        if (isNullLectureError) {
                            console.warn(`Orphaned LecturesTopics records detected for topic ${topicId}. Continuing with partial data.`);
                        } else {
                            console.error("Error fetching lecture topics page:", error);
                        }

                        captureError(error, {
                            feature: 'lectures',
                            operation: 'fetch-lecture-topics-page',
                            context: {
                                topicId,
                                currentPage: allItems.length,
                                isOrphanedRecordError: isNullLectureError
                            }
                        });

                        // Try to extract partial data from the error response
                        let partialItems = [];
                        if (error.data?.listLecturesTopics?.items) {
                            // Filter out any items with null lecture fields
                            partialItems = error.data.listLecturesTopics.items.filter(item => item?.lecture?.id);
                        }

                        // Return accumulated items plus any valid partial data
                        return [...allItems, ...partialItems];
                    }
                };
                
                try {
                    // Use Promise.all to fetch all topics concurrently
                    const topicResults = await Promise.all(filters.selectedTopics.map(async (topicId) => {
                        const items = await fetchAllLectureTopics(topicId);
                        return items.map(item => item?.lecture?.id).filter(Boolean);
                    }));
                    
                    // Flatten the array of arrays and remove duplicates
                    lecturesinTopicIds = [...new Set(topicResults.flat())];
                } catch (error) {
                    console.error("Error in topic filtering process:", error);
                }
                
                if (lecturesinTopicIds.length > 0) {
                    searchLectureFilter.and.push({ or: lecturesinTopicIds.map((lectureId) => ({ id: { eq: lectureId } })) });
                } else {
                    // If no matching lectures found, add an impossible condition to return no results
                    searchLectureFilter.and.push({ id: { eq: "none" } });
                }
            }

            const fetchAllLectures = async (filter, allItems = [], nextToken = null) => {
                try {
                    const response = await client.graphql({
                        variables: {
                            filter: filter,

                            limit: 100,
                            nextToken: nextToken
                        },
                        query: /* GraphQL */ `
                            query SearchLectures($filter: SearchableLectureFilterInput, $limit: Int, $nextToken: String, $sort: [SearchableLectureSortInput]) {
                                searchLectures(filter: $filter, limit: $limit, nextToken: $nextToken, sort: $sort) {
                                    items {
                                        id
                                        createdAt
                                        approved
                                        title {
                                            language
                                            text
                                        }
                                        content {
                                            language
                                            text
                                        }
                                        speakers {
                                            items {
                                                id
                                                employee {
                                                    image {
                                                        fileName
                                                        alt
                                                    }
                                                    user {
                                                        firstName
                                                        lastName
                                                    }
                                                }
                                            }
                                        }
                                        speakersUsers {
                                            items {
                                                id
                                                user {
                                                    id
                                                    firstName
                                                    lastName
                                                    image {
                                                        fileName
                                                        alt
                                                    }
                                                }
                                            }
                                        }
                                        company {
                                            id
                                            name
                                            image {
                                                main {
                                                    fileName
                                                    alt
                                                }
                                            }
                                        }
                                        topics {
                                            items {
                                                topic {
                                                    name {
                                                        text
                                                        language
                                                    }
                                                }
                                            }
                                        }
                                        type
                                        companyBooth
                                        room
                                        startTime
                                        endTime
                                        event {
                                            id
                                            type
                                            name {
                                                language
                                                text
                                            }
                                            image {
                                                fileName
                                            }
                                        }
                                        image {
                                            fileName
                                        }
                                        pdfDe {
                                            fileName
                                            s3Path
                                        }
                                        pdfEn {
                                            fileName
                                            s3Path
                                        }
                                        videoS3Key {
                                            s3Path
                                        }
                                    }
                                    nextToken
                                    total
                                }
                            }
                        `
                    });

                    const newItems = [...allItems, ...response.data.searchLectures.items];
                    
                    // If there's a nextToken, fetch the next page
                    if (response.data.searchLectures.nextToken) {
                        return fetchAllLectures(filter, newItems, response.data.searchLectures.nextToken);
                    }
                    
                    return { items: newItems, total: response.data.searchLectures.total };
                } catch (error) {
                    console.error("Error fetching lectures page:", error);
                    // Return what we have so far in case of error
                    return { items: allItems, error: error };
                }
            };

            // Start the process
            const response = await fetchAllLectures(searchLectureFilter);

            // Helper function to extract numeric part of room
            const getRoomNumber = (room) => {
                if (!room) return null;
                const matches = room.match(/\d+/);
                return matches ? parseInt(matches[0], 10) : null;
            };

            const sortedLectures = response.items.sort((a, b) => {
                // Handle room sorting first
                const roomA = a.room || '';
                const roomB = b.room || '';
                
                // If both have no room, sort by startTime
                if (!roomA && !roomB) {
                    if (!a.startTime && !b.startTime) return 0;
                    if (!a.startTime) return 1;
                    if (!b.startTime) return -1;
                    return a.startTime.localeCompare(b.startTime);
                }
                
                // Move lectures without room to the end
                if (!roomA) return 1;
                if (!roomB) return -1;
                
                // Compare rooms numerically if they contain numbers
                const roomNumA = getRoomNumber(roomA);
                const roomNumB = getRoomNumber(roomB);
                
                if (roomNumA !== null && roomNumB !== null) {
                    const numComparison = roomNumA - roomNumB;
                    if (numComparison !== 0) return numComparison;
                } else {
                    // If not numeric or mixed, fall back to string comparison
                    const roomComparison = roomA.localeCompare(roomB);
                    if (roomComparison !== 0) return roomComparison;
                }
                
                // Same room, sort by startTime
                if (!a.startTime && !b.startTime) return 0;
                if (!a.startTime) return 1;
                if (!b.startTime) return -1;
                return a.startTime.localeCompare(b.startTime);
            });
            
            setLectures(sortedLectures);
        } catch (error) {
            console.error("Error fetching lectures:", error);
            captureError(error, {
                feature: 'lectures',
                operation: 'fetch-lectures',
                context: {
                    eventId: filters.selectedEventId,
                    selectedTopicsCount: filters.selectedTopics?.length,
                    selectedTypesCount: filters.selectedTypes?.length,
                    companiesCount: selectedCompanies.length
                }
            });
            const lectures = error.data?.searchLectures?.items || [];
            if (lectures.length > 0) {
                // Helper function to extract numeric part of room (duplicate for error case)
                const getRoomNumber = (room) => {
                    if (!room) return null;
                    const matches = room.match(/\d+/);
                    return matches ? parseInt(matches[0], 10) : null;
                };
                
                const sortedLectures = lectures.sort((a, b) => {
                    // Handle room sorting first
                    const roomA = a.room || '';
                    const roomB = b.room || '';
                    
                    // If both have no room, sort by startTime
                    if (!roomA && !roomB) {
                        if (!a.startTime && !b.startTime) return 0;
                        if (!a.startTime) return 1;
                        if (!b.startTime) return -1;
                        return a.startTime.localeCompare(b.startTime);
                    }
                    
                    // Move lectures without room to the end
                    if (!roomA) return 1;
                    if (!roomB) return -1;
                    
                    // Compare rooms numerically if they contain numbers
                    const roomNumA = getRoomNumber(roomA);
                    const roomNumB = getRoomNumber(roomB);
                    
                    if (roomNumA !== null && roomNumB !== null) {
                        const numComparison = roomNumA - roomNumB;
                        if (numComparison !== 0) return numComparison;
                    } else {
                        // If not numeric or mixed, fall back to string comparison
                        const roomComparison = roomA.localeCompare(roomB);
                        if (roomComparison !== 0) return roomComparison;
                    }
                    
                    // Same room, sort by startTime
                    if (!a.startTime && !b.startTime) return 0;
                    if (!a.startTime) return 1;
                    if (!b.startTime) return -1;
                    return a.startTime.localeCompare(b.startTime);
                });
                setLectures(sortedLectures);
            }
        }
        setIsLoadingLectures(false);
    };


// Fetch active events
useEffect(() => {
    if(user?.id && currentIndustry) {
        fetchEvents();
    }
}, [user, currentIndustry]);


// Fetch lectures
useEffect(() => {
    if (events.length > 0) {
        fetchLectures();
    } else {
        setLectures([]);
        setIsLoadingLectures(false);
    }
}, [events, filters.selectedEventId, filters.selectedTypes, filters.selectedTopics, filters.pdf, filters.video, selectedCompanies]);

// Fetch topics
useEffect(() => {
    if (topicGroups.length === 0) {
        getTopics();
    }
}, [topicGroups]);

// Fetch companies
useEffect(() => {
    if (debouncedCompanySearchTerm.length >= 2) {
        searchCompanies(debouncedCompanySearchTerm);
    }
}, [debouncedCompanySearchTerm]);

    const navigate = useNavigate();

    const openLecture = (lectureId) => {
        navigate(`/lecture/${lectureId}`);
    };

    const downloadFile = async (fileName, path) => {
        const regex = /protected\/(eu-central-1:[a-z0-9-]+)\//i;
        const match = path.match(regex);
        const identity = match[1];
        const getUrlResult = await getUrl({
            key: fileName,
            options: {
            accessLevel: 'protected' ,
            targetIdentityId: identity,
            expires: 60,
            },
        });
        console.log('signed URL: ', getUrlResult.url);
        window.open(getUrlResult.url, '_blank');
    }

    const isLectureClickable = (lecture) => {
        return lecture.videoS3Key?.s3Path || lecture.content;
    };
    
    const hasVideoAccess = (lecture) => {
        return user.hasInsightAccess || lecture.event?.type === "seminar";
    };

    // Filter lectures based on search term
    const filteredLectures = lectures.filter(lecture => {
        if (!searchTerm || searchTerm.trim() === '') {
            return true;
        }
        
        const searchTermLower = searchTerm.toLowerCase().trim();
        const lectureTitle = getLocalizedText(lecture.title).toLowerCase();
        
        return lectureTitle.includes(searchTermLower);
    });

    return (
<div style={{maxWidth: "1500px"}} className="mx-auto my-5">

        {/* If dev mode, show user hasInsightAccess and events he has tickets for */}
        {isDevMode && user && (
            <MDBCard className="mb-3">
                <MDBCardBody>
                    <MDBCardTitle>{t("User Information")}</MDBCardTitle>
                    <MDBCardText>
                        <strong>{t("User ID")}: </strong>{user.id}<br />
                        <strong>{t("Has Insight Access")}: </strong>{user.hasInsightAccess ? t("Yes") : t("No")}<br />
                        <strong>{t("Events with Tickets")}: </strong>
                        <ul>
                            {userAttendedEvents.map(event => (
                                <li key={event.id}>
                                    {getLocalizedText(event.name)} ({event.startDate})
                                </li>
                            ))}
                        </ul>
                    </MDBCardText>
                    <MDBBtn onClick={() => console.log(events)}>Log Events</MDBBtn>
                </MDBCardBody>
            </MDBCard>
        )}

    {user && !user.hasInsightAccess && currentIndustry?.id === LOUNGES_INDUSTRY_ID &&
    <MDBCard className="mb-3">
        <MDBRow>
        <MDBCol md='8'>
        <MDBCardBody>
            <MDBCardTitle>{t("Book your access to the lecture recordings")}</MDBCardTitle>
            <MDBCardText>
                {hasLounges2026ScannedTicket ? <>
                {t("Get access to the lecture recordings from the LOUNGES 2026.")}
                <MDBTypography tag="span" className="d-block mt-2">
                    {t("As an on-site visitor of the LOUNGES 2026, you receive the visitor price")}:
                </MDBTypography>
                <MDBTypography tag="strong" className="d-block mt-1">
                    <s className="text-muted me-2">{LOUNGES_2026_INSIGHT_NON_VISITOR_PRICE.toFixed(2)}€</s>
                    {LOUNGES_2026_INSIGHT_VISITOR_PRICE.toFixed(2)}€
                </MDBTypography>
            </>
            :
            <>
            {t("Get access to the lecture recordings from the LOUNGES 2026. The price is")}:
                <MDBTypography tag="strong" className="d-block mt-2">
                    {t("Standard Price")} - {LOUNGES_2026_INSIGHT_NON_VISITOR_PRICE.toFixed(2)}€
                </MDBTypography>
                </>
            }
            </MDBCardText>
            <MDBBtn
            onClick={() => {
                const productId = hasLounges2026ScannedTicket
                    ? LOUNGES_2026_INSIGHT_VISITOR_PRODUCT_ID
                    : LOUNGES_2026_INSIGHT_NON_VISITOR_PRODUCT_ID;
                navigate(`/checkout?productId=${productId}`);
            }}
            >{t("Book now")}</MDBBtn>
        </MDBCardBody>
        </MDBCol>
        <MDBCol md='4'>
            <MDBCardImage
                src={lounges2026Event?.image?.fileName
                    ? `https://iec-cp-public.s3.eu-central-1.amazonaws.com/EventImages/${lounges2026Event.image.fileName}`
                    : "https://placehold.co/600x400?text=LOUNGES+2026"}
                alt={lounges2026Event?.image?.alt || t("Lecture Recordings")}
                className="img-fluid rounded"
                style={{ objectFit: "cover", height: "100%", width: "100%" }}
                />
            </MDBCol>
        </MDBRow>

  </MDBCard>
  }
<MDBCard className="mb-3">
    <MDBCardBody className="p-3">
       <MDBRow>
        <MDBCol md='3'>
            <MDBCardTitle>{t("Lectures")}</MDBCardTitle>
        </MDBCol>
        <MDBCol md='9'>
            <MDBInput
                label={t("Search")}
                type='text'
                onChange={(e) => setSearchTerm(e.target.value)}
                value={searchTerm}
            />
        </MDBCol>
       </MDBRow>
    </MDBCardBody>
</MDBCard>
<MDBRow>
    <MDBCol lg='3'>
    <MDBAccordion alwaysOpen initialActive={[1,2,3,4,5]}>
        <MDBAccordionItem collapseId={1} headerTitle={t("Event")}>
            {events?.length > 0 ? events.map((event, index) => (
            <MDBRadio
                key={index}
                name='eventSelection'
                id={`event-${event.id}`}
                label={getLocalizedText(event.name)}
                checked={filters.selectedEventId === event.id}
                onChange={() => setFilters({...filters, selectedEventId: event.id})}
                />
            )) : (
                <small className="text-muted d-block">
                    {t("No events yet")}
                </small>
            )}
            <small className="text-muted d-block mt-2">
                {t("You only see events you have attended with a ticket.")}
            </small>
        </MDBAccordionItem>
        <MDBAccordionItem collapseId={2} headerTitle={t("Lecture Type")}>
            <MDBCheckbox
                name='typeMarketing'
                id='typeMarketing'
                label={t("Marketing")}
                checked={filters.selectedTypes?.includes("marketing")}
                onChange={(e) => {
                const newSelectedTypes = e.target.checked 
                    ? [...(filters.selectedTypes || []), "marketing"]
                    : (filters.selectedTypes || []).filter(type => type !== "marketing");
                setFilters({...filters, selectedTypes: newSelectedTypes});
                }}
            />
            <MDBCheckbox
                name='typeTechnical'
                id='typeTechnical'
                label={t("Technical")}
                checked={filters.selectedTypes?.includes("technical")}
                onChange={(e) => {
                const newSelectedTypes = e.target.checked 
                    ? [...(filters.selectedTypes || []), "technical"]
                    : (filters.selectedTypes || []).filter(type => type !== "technical");
                setFilters({...filters, selectedTypes: newSelectedTypes});
                }}
            />
            <MDBCheckbox
                name='typeEducational'
                id='typeEducational'
                label={t("Educational")}
                checked={filters.selectedTypes?.includes("educational")}
                onChange={(e) => {
                const newSelectedTypes = e.target.checked 
                    ? [...(filters.selectedTypes || []), "educational"]
                    : (filters.selectedTypes || []).filter(type => type !== "educational");
                setFilters({...filters, selectedTypes: newSelectedTypes});
                }}
            />
        </MDBAccordionItem>
        {currentIndustry?.id === "ec37b0b2-7f72-4de4-b12b-5eac5ff71ae3" &&
        <MDBAccordionItem collapseId={3} headerTitle={t("Category")}>
        <MDBAccordion flush borderless className="accordion-slim">
            {topicGroups?.map((group, index) => (
                <MDBAccordionItem
                    key={index}
                    collapseId={"category" + index + 1}
                    headerTitle={
                    <MDBCheckbox
                        label={getLocalizedText(group.name, i18n.language)}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleClickTopicGroup(group.id);
                          }}
                        checked={group.topics.items.every(topic => filters.selectedTopics?.includes(topic.id))}
                    />}
                    bodyStyle={{ padding: "0", paddingBottom: "0.5rem" }}
                > 
                    {group.topics.items.map((topic, index) => (
                        <MDBCheckbox
                            key={index}
                            wrapperClass="ms-3"
                            name='flexCheck'
                            id='flexCheckDefault'
                            label={getLocalizedText(topic.name, i18n.language)}
                            onClick={() => handleClickTopic(topic.id)}
                            checked={filters.selectedTopics?.includes(topic.id)}
                        />
                    ))
                    }
                </MDBAccordionItem>
            ))}
        </MDBAccordion>
        </MDBAccordionItem>
        }
        <MDBAccordionItem collapseId={4} headerTitle={t("Company")}>
        <div className="position-relative mb-3">
                <MDBInput
                    label={t("Search company")}
                    type="text"
                    value={companySearchTerm}
                    onChange={(e) => setCompanySearchTerm(e.target.value)}
                    onBlur={handleCompanyInputBlur}
                    className="mb-1"
                />
                
                {companySearchResults.length > 0 && (
                    <>
                        <div className="position-absolute w-100 border rounded bg-white z-3" style={{ maxHeight: "300px", overflowY: "auto" }}>
                            {companySearchResults.map((company) => (
                                <div
                                    key={company.id}
                                    className="d-flex align-items-center p-2 cursor-pointer hover-opacity-75"
                                    style={{ cursor: "pointer" }}
                                    onClick={() => addCompany(company)}
                                >
                                    {company.image?.main?.fileName ? (
                                        <img
                                            src={`https://iec-cp-public.s3.eu-central-1.amazonaws.com/companyImages/${company.image.main.fileName}`}
                                            alt={company.name}
                                            className="me-2 rounded-circle border"
                                            style={{ width: "30px", height: "30px", objectFit: "contain" }}
                                        />
                                    ) : 
                                    (
                                        <div className="me-2 rounded-circle border d-flex align-items-center justify-content-center bg-light" style={{ width: "30px", height: "30px" }}>
                                            <MDBIcon fas icon="building" size="sm" />
                                        </div>
                                    )}
                                    <span>{company.name}</span>
                                </div>
                            ))}
                        </div>
                        {/* This div creates space for the dropdown */}
                        <div style={{ height: `${companySearchResults.length * 60}px` }}></div>
                    </>
                )}
            </div>
            
            {/* Display selected companies as checkboxes */}
            {selectedCompanies.length > 0 && (
                <div className="mt-3">
                    <small className="text-muted mb-2 d-block">{t("Selected companies")}:</small>
                    {selectedCompanies.map((company) => (
                        <MDBCheckbox
                            key={company.id}
                            name={`company-${company.id}`}
                            id={`company-${company.id}`}
                            label={
                                <div className="d-flex align-items-center">
                                    {company.image?.main?.fileName ? (
                                        <img
                                            src={`https://iec-cp-public.s3.eu-central-1.amazonaws.com/companyImages/${company.image.main.fileName}`}
                                            alt={company.name}
                                            className="me-2 rounded-circle border"
                                            style={{ width: "20px", height: "20px", objectFit: "contain" }}
                                        />
                                    ) : (
                                        <MDBIcon fas icon="building" className="me-2" size="sm" />
                                    )}
                                    <span>{company.name}</span>
                                </div>
                            }
                            checked={true}
                            onChange={() => removeCompany(company.id)}
                        />
                    ))}
                </div>
            )}
        </MDBAccordionItem>
        <MDBAccordionItem collapseId={5} headerTitle={t("Media")}>
            {/* <MDBCheckbox
                name='flexCheck'
                id='flexCheckDefault'
                label={<span><MDBIcon icon="file-pdf" className="me-2" />{t("Documents available")}</span>}
                onClick={(e) => setFilters({ ...filters, pdf: e.target.checked })}
                checked={filters.pdf}
            /> */}
            <MDBCheckbox
            name='flexCheck'
            id='flexCheckDefault'
            label={<span><MDBIcon icon="video" className="me-2" />{t("Video available")}</span>}
            onClick={(e) => setFilters({ ...filters, video: e.target.checked })}
            checked={filters.video}
            />
        </MDBAccordionItem>
        </MDBAccordion>
    </MDBCol>
    <MDBCol lg='9'>
        {/* <MDBCard</MDBCard>>
            <MDBCardBody>    */}
            {/* {currentIndustry.id} */}
                {isLoadingLectures ?
                 <div className="d-flex justify-content-center w-100">
                 <MDBSpinner className="mt-5" style={{ width: '3rem', height: '3rem' }} role='status'>
                     <span className='visually-hidden'>{t("Loading...")}</span>
                 </MDBSpinner>
                </div> :
                events?.length === 0 ? (
                    <div className="text-center my-5 py-5">
                        <MDBIcon fas icon="chalkboard-teacher" size="4x" className="text-muted mb-4" style={{ fontSize: "5rem" }} />
                        <h4>{t("No lecture recordings available yet")}</h4>
                        <p className="text-muted mt-3 mx-auto" style={{ maxWidth: "520px" }}>
                            {t("Attend one of our events with a ticket to access the corresponding lecture recordings and documents here afterwards.")}
                        </p>
                    </div>
                ) :
                filteredLectures?.length === 0 ? (
                    <div className="text-center my-5">
                        <MDBIcon fas icon="chalkboard-teacher" size="3x" className="text-muted mb-3" />
                        <h5>{t("No lectures found")}</h5>
                    </div>
                ) :
                <MDBRow>
                    {filteredLectures?.map((lecture, index) => (
                        <MDBCol key={index} size={12}>
                            <MDBCard className="my-2">
                                <MDBRow className='g-0'>
                                    <MDBCol md='4' className="d-flex align-items-center justify-content-center">
                                    {lecture.image?.fileName?
                                    <MDBCardImage
                                    src={lecture.image?.fileName ? "https://iec-cp-public.s3.eu-central-1.amazonaws.com/lectureImages/" + lecture.image.fileName : undefined}
                                    alt={getLocalizedText(lecture.title)}
                                    className='rounded'
                                    style={{ aspectRatio: "16 / 9" , objectFit: "cover", cursor: isLectureClickable(lecture) ? "pointer" : "default" }}
                                    onClick={isLectureClickable(lecture) ? () => openLecture(lecture.id) : undefined}

                                    fluid />
                                    : 
                                    lecture.event?.image?.fileName ?
                                    <MDBCardImage
                                    src={lecture.event.image.fileName ? "https://iec-cp-public.s3.eu-central-1.amazonaws.com/EventImages/" + lecture.event.image.fileName : undefined}  
                                    alt={getLocalizedText(lecture.title)}
                                    className='rounded'
                                    style={{ aspectRatio: "16 / 9" , objectFit: "cover", cursor: isLectureClickable(lecture) ? "pointer" : "default" }}
                                    onClick={isLectureClickable(lecture) ? () => openLecture(lecture.id) : undefined}
                                    fluid />
                                    :
                                    <MDBIcon className="" style={{fontSize: "50px" }} fas icon="chalkboard-teacher" />
                                    }

                                    </MDBCol>
                                    <MDBCol md='6'>
                                    <MDBCardBody>
                                        <MDBCardTitle onClick={isLectureClickable(lecture) ? () => openLecture(lecture.id) : undefined}  style={{cursor: isLectureClickable(lecture) ? "pointer" : "default"}}>{getLocalizedText(lecture.title)}</MDBCardTitle>
                                        <MDBCardText>
                                        <div className="d-flex flex-row my-3">
                                            <div className="me-3">
                                                {lecture.company?.image?.main?.fileName &&
                                                <img
                                                    src={lecture.company?.image?.main?.fileName ? "https://iec-cp-public.s3.eu-central-1.amazonaws.com/companyImages/" + lecture.company.image.main.fileName : undefined}
                                                    alt={lecture.company?.name}
                                                    className="img-fluid my-1"
                                                    style={{ height: "32px", width: "32px", objectFit: "contain" }}
                                                />
                                            }
                                            </div>
                                            {/* <div className="my-auto me-3 bg-primary rounded-circle" style={{height: "5px", width: "5px"}}></div> */}
                                                {lecture.speakers?.items?.map((speaker, index) => (
                                                    <MDBChip key={speaker.id} size='sm'  style={{cursor: "default"}}>
                                                    <img
                                                    src={speaker?.employee?.image?.fileName ? "https://iec-cp-public.s3.eu-central-1.amazonaws.com/userImages/" + speaker?.employee?.image?.fileName : undefined}
                                                    alt={speaker?.employee?.user?.firstName + " " + speaker?.employee?.user?.lastName}
                                                    className="rounded-circle"
                                                    style={{ objectFit: "cover", aspectRatio: "1 / 1" }}
                                                    />
                                                    {speaker?.employee?.user?.firstName} {speaker?.employee?.user?.lastName}
                                                  </MDBChip>
                                                ))}
                                                {lecture.speakersUsers?.items?.map((speakerUser) => (
                                                    <MDBChip key={`user-${speakerUser.id}`} size='sm' style={{cursor: "default"}}>
                                                    <img
                                                    src={speakerUser?.user?.image?.fileName ? "https://iec-cp-public.s3.eu-central-1.amazonaws.com/userImages/" + speakerUser.user.image.fileName : undefined}
                                                    alt={speakerUser?.user?.firstName + " " + speakerUser?.user?.lastName}
                                                    className="rounded-circle"
                                                    style={{ objectFit: "cover", aspectRatio: "1 / 1" }}
                                                    />
                                                    {speakerUser?.user?.firstName} {speakerUser?.user?.lastName}
                                                  </MDBChip>
                                                ))}
                                        </div>
                                        </MDBCardText>
                                        <MDBCardText>
                                        <small className='text-muted'>
                                        {lecture.topics?.items?.map((topic, index) => (
                                            <span key={index} className="me-2" size="sm">
                                                {getLocalizedText(topic.topic.name)}
                                            </span>
                                        ))}
                                        </small>
                                        </MDBCardText>
                                    </MDBCardBody>
                                    <MDBCardFooter className='text-muted border-0 pb-0'>
                                        <div className="d-flex justify-content-between">
                                            <span>{getLocalizedText(lecture.event?.name)}</span>
                                            {lecture.startTime &&<span>{new Date(lecture.startTime).toLocaleDateString(i18n.language, { year: 'numeric', month: '2-digit', day: '2-digit' })} {new Date(lecture.startTime).toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' })}</span>}
                                            {lecture.room &&<span>{t("Room") + " " + lecture.room}</span>}
                                        </div>
                                    </MDBCardFooter>
                                    </MDBCol>
                                    <MDBCol md='2' className="text-center d-flex align-items-center justify-content-center">
                                        <MDBCardBody>
                                            {lecture.pdfDe?.fileName || lecture.pdfEn?.fileName ?
                                            <>
                                            <MDBBtn
                                            outline
                                            color="secondary"
                                            size="sm"
                                            disabled={userAttendedEvents.find(e => e.id === lecture.event?.id) ? false : !user.hasInsightAccess}
                                            onClick={() => downloadFile(lecture.pdfDe?.fileName || lecture.pdfEn?.fileName, lecture.pdfDe?.s3Path || lecture.pdfEn?.s3Path)}
                                            >
                                                <MDBIcon fas icon="download" /> {t("Document")}
                                            </MDBBtn>
                                            <br/></>
                                            : null}
                                            
                                            {lecture.videoS3Key?.s3Path ?
                                            <MDBBtn
                                            className="mt-2"
                                            outline
                                            color="secondary"
                                            size="sm"
                                            disabled={!hasVideoAccess(lecture)}
                                            onClick={() => openLecture(lecture.id)}
                                            >
                                                <MDBIcon fas icon="video" /> {t("Video")}
                                            </MDBBtn>
                                            : null}
                                        </MDBCardBody>
                                    </MDBCol>
                                </MDBRow>
                            </MDBCard>
                        </MDBCol>
                    ))}
                </MDBRow>
                }
            {/* </MDBCardBody>
        </MDBCard> */}
    </MDBCol>
</MDBRow>
</div>
    );
}